// Horizontal calendar timeline for an on-call rotation. The PagerDuty/
// Datadog-style "bars per shift" view — each bar is a user's slot,
// colour-coded, positioned by time.
//
// Layout:
//   - Top strip: day labels with vertical guides
//   - Bar strip: shift rectangles, absolutely positioned by % of range
//   - Overlay: optional override band (replaces rotation user during
//     the override window), now-line vertical indicator
//
// Hover any bar for an exact start/end tooltip. PR 3 will add a click
// handler on future shifts to launch the override flow.
//
// Time math lives in src/lib/schedules.ts (rotationSlotsInRange) so the
// UI matches the engine's view byte-for-byte.

import { useMemo } from 'react';
import type { Schedule } from '../../lib/types';
import {
  colourForUser,
  effectiveOverrides,
  resolveOnCallUser,
  rotationSlotsInRange,
  type RotationSlot,
} from '../../lib/schedules';

interface Props {
  schedule: Schedule;
  /** Start of the visible window (ms). Bars before this are clipped. */
  fromMs: number;
  /** End of the visible window (ms). Bars after this are clipped. */
  toMs: number;
  /** Visual height of the bar strip. Day-label strip adds its own height. */
  height?: number;
  /** Future PR 3 hook — click a future shift to launch override. */
  onSlotClick?: (slot: RotationSlot) => void;
}

const BAR_HEIGHT = 44;
const HEADER_HEIGHT = 24;

export function ScheduleTimeline({
  schedule,
  fromMs,
  toMs,
  height = BAR_HEIGHT,
  onSlotClick,
}: Props) {
  // Pre-compute everything that depends on the range so the render path
  // is pure positioning. The day strip, rotation slots, override band
  // and now-line are all coordinates in the same shared range.
  const totalMs = Math.max(1, toMs - fromMs);
  const now = Date.now();

  const rotationSlots = useMemo(
    () => rotationSlotsInRange(schedule, fromMs, toMs),
    [schedule, fromMs, toMs],
  );

  // If an override is active, the rotation underneath is hypothetical
  // for that window. Reflect that by clipping the rotation bars to
  // OUTSIDE the override window and rendering a single override bar
  // inside it — what the bars represent should match who'd actually be
  // paged at any given moment.
  // Every override that overlaps the visible window. Each is clipped
  // to the window so the rendered band fits inside the bar strip.
  // Overrides are non-overlapping (enforced at write time) so we can
  // slice rotation slots by them iteratively without ambiguity.
  const overrideRanges = effectiveOverrides(schedule)
    .filter((ov) => ov.untilMs > fromMs && ov.fromMs < toMs)
    .map((ov) => ({
      id: ov.id ?? `legacy-${ov.fromMs}`,
      startMs: Math.max(fromMs, ov.fromMs),
      endMs: Math.min(toMs, ov.untilMs),
      userId: ov.userId,
      displayName: ov.userDisplayName ?? ov.userId,
    }));

  // Subtract each override window from the rotation slot list. After
  // the loop, `visibleRotationSlots` contains every rotation slice
  // outside any override.
  const visibleRotationSlots = overrideRanges.reduce<RotationSlot[]>((slots, range) => {
    return slots.flatMap((s) => {
      const startsBefore = s.startMs < range.startMs;
      const endsAfter = s.endMs > range.endMs;
      const overlapsOverride =
        s.endMs > range.startMs && s.startMs < range.endMs;
      if (!overlapsOverride) return [s];
      const out: RotationSlot[] = [];
      if (startsBefore) out.push({ ...s, endMs: range.startMs });
      if (endsAfter) out.push({ ...s, startMs: range.endMs });
      return out;
    });
  }, rotationSlots);

  const days = useMemo(() => dayBoundaries(fromMs, toMs), [fromMs, toMs]);
  const nowResolution = resolveOnCallUser(schedule, now);

  function pct(ms: number): number {
    return ((ms - fromMs) / totalMs) * 100;
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Day-label strip ----------------------------------------- */}
      <div
        style={{
          position: 'relative',
          height: HEADER_HEIGHT,
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {days.map((d) => (
          <div
            key={d.ms}
            style={{
              position: 'absolute',
              left: `${pct(d.ms)}%`,
              top: 0,
              bottom: 0,
              borderLeft: '1px dashed var(--color-border)',
              paddingLeft: 6,
              fontSize: 'var(--fs-xs)',
              color: 'var(--color-text-tertiary)',
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {d.label}
          </div>
        ))}
        {/* "Now" pill — anchored to the same horizontal position as
            the now-line below. Sits in the day-header strip so it
            doesn't overlap any bars. */}
        {now >= fromMs && now <= toMs && (
          <div
            style={{
              position: 'absolute',
              left: `${pct(now)}%`,
              top: 2,
              transform: 'translateX(-50%)',
              background: 'var(--color-accent)',
              color: 'white',
              fontSize: 10,
              fontWeight: 'var(--fw-semibold)',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              padding: '1px 6px',
              borderRadius: 'var(--radius-full)',
              lineHeight: 1.4,
              pointerEvents: 'none',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
              whiteSpace: 'nowrap',
            }}
            aria-hidden
          >
            Now
          </div>
        )}
      </div>

      {/* Bar strip ------------------------------------------------- */}
      <div
        style={{
          position: 'relative',
          height,
          background: 'var(--color-surface)',
          overflow: 'hidden',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        {/* Day guide lines (faint vertical dashes between days) */}
        {days.slice(1).map((d) => (
          <div
            key={`guide-${d.ms}`}
            style={{
              position: 'absolute',
              left: `${pct(d.ms)}%`,
              top: 0,
              bottom: 0,
              width: 1,
              background: 'var(--color-border)',
              opacity: 0.4,
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* Rotation bars */}
        {visibleRotationSlots.map((s, i) => {
          const widthPct = ((s.endMs - s.startMs) / totalMs) * 100;
          const isFuture = s.startMs > now;
          return (
            <button
              key={`slot-${i}-${s.startMs}`}
              type="button"
              onClick={() => onSlotClick?.(s)}
              disabled={!isFuture || !onSlotClick}
              title={`${s.displayName} · ${formatRange(s.startMs, s.endMs)}`}
              style={{
                position: 'absolute',
                left: `${pct(s.startMs)}%`,
                width: `${widthPct}%`,
                top: 4,
                bottom: 4,
                background: colourForUser(s.userId),
                // De-emphasise past shifts so the visual focus is on
                // current + future on-call coverage.
                opacity: s.endMs < now ? 0.35 : 1,
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                color: 'white',
                fontSize: 'var(--fs-xs)',
                fontWeight: 'var(--fw-medium)',
                textAlign: 'left',
                paddingLeft: 6,
                paddingRight: 6,
                cursor: isFuture && onSlotClick ? 'pointer' : 'default',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {widthPct > 6 ? s.displayName : ''}
            </button>
          );
        })}

        {/* Override bands — one per pending/active override in the
            visible range. Striped overlay with a label pill that
            gracefully degrades at narrow widths (truncates with
            ellipsis; tooltip always carries the full detail). */}
        {overrideRanges.map((range) => (
          <div
            key={range.id}
            title={`Override: ${range.displayName} · until ${formatTime(range.endMs)}`}
            style={{
              position: 'absolute',
              left: `${pct(range.startMs)}%`,
              width: `${((range.endMs - range.startMs) / totalMs) * 100}%`,
              top: 4,
              bottom: 4,
              background: `repeating-linear-gradient(45deg, ${colourForUser(range.userId)} 0 8px, rgba(255,255,255,0.12) 8px 16px)`,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid rgba(255,255,255,0.18)',
              color: 'white',
              fontSize: 'var(--fs-xs)',
              fontWeight: 'var(--fw-semibold)',
              padding: '0 4px',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                background: 'rgba(0,0,0,0.45)',
                borderRadius: 'var(--radius-sm)',
                padding: '1px 6px',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <span aria-hidden style={{ fontSize: 10 }}>⛨</span>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {range.displayName}
              </span>
            </span>
          </div>
        ))}

        {/* Now-line — only renders if `now` falls inside the window.
            Accent (purple) rather than danger (red) so it doesn't read
            as "incident happening right now"; the "Now" tag above the
            bar strip makes the meaning explicit. */}
        {now >= fromMs && now <= toMs && (
          <div
            aria-label={`Current time, ${nowResolution?.displayName ?? 'unknown'} on call`}
            style={{
              position: 'absolute',
              left: `${pct(now)}%`,
              top: 0,
              bottom: 0,
              width: 2,
              background: 'var(--color-accent)',
              boxShadow: '0 0 6px rgba(124, 92, 255, 0.6)',
              pointerEvents: 'none',
              transform: 'translateX(-1px)',
            }}
          />
        )}
      </div>
    </div>
  );
}


/**
 * Generates day-boundary labels for the header strip. Returns the
 * first visible day plus every subsequent midnight in range, with a
 * short label appropriate to range length (showing weekday for short
 * ranges, day-of-month for longer ones).
 */
function dayBoundaries(fromMs: number, toMs: number): Array<{ ms: number; label: string }> {
  const rangeDays = (toMs - fromMs) / (24 * 60 * 60 * 1000);
  const showWeekday = rangeDays <= 14; // ≤2 weeks → weekday label
  const out: Array<{ ms: number; label: string }> = [];
  // First label: the visible-window start moment, formatted as a day.
  const start = new Date(fromMs);
  start.setHours(0, 0, 0, 0);
  // Walk through midnights from `start` up to `toMs`.
  let cursor = start.getTime();
  if (cursor < fromMs) {
    // First visible label is the first midnight at or after fromMs.
    cursor += 24 * 60 * 60 * 1000;
  }
  while (cursor < toMs) {
    out.push({ ms: cursor, label: labelForDay(cursor, showWeekday) });
    cursor += 24 * 60 * 60 * 1000;
  }
  return out;
}

function labelForDay(ms: number, showWeekday: boolean): string {
  const d = new Date(ms);
  if (showWeekday) {
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRange(startMs: number, endMs: number): string {
  return `${formatTime(startMs)} → ${formatTime(endMs)}`;
}
